import React, { useState, useEffect, useCallback, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import AuthContext from '../context/AuthContext';
import api from '../services/api';
import { toast } from 'sonner';
import { 
  Building2, Eye, FileText, TrendingUp, 
  Plus, Check, X, Clock, Calendar, RefreshCw,
  ChevronLeft, Home, Bell, Settings, LogOut,
  Users, BarChart3, AlertCircle, Search, Filter
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  Dialog, DialogContent, DialogDescription, DialogFooter, 
  DialogHeader, DialogTitle, DialogTrigger 
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import AgentPerformanceScore from '../components/AgentPerformanceScore';
import AEDIcon from '../components/icons/AEDIcon';

const formatCurrency = (amount) => {
  if (!amount) return 'N/A';
  return new Intl.NumberFormat('en-AE', {
    style: 'currency',
    currency: 'AED',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount);
};

// Agent Status Banner
const AgentStatusBanner = ({ status, agencyName }) => {
  if (status === 'approved') {
    return (
      <div className="bg-green-950/20 border border-green-600/30 rounded-lg p-4 mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-green-600/20 flex items-center justify-center">
            <Check className="w-5 h-5 text-green-400" />
          </div>
          <div>
            <p className="font-medium text-green-400">Agent Verified</p>
            <p className="text-sm text-muted-foreground">{agencyName}</p>
          </div>
        </div>
      </div>
    );
  }
  
  if (status === 'pending') {
    return (
      <div className="bg-gold-950/20 border border-gold-600/30 rounded-lg p-4 mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-gold-600/20 flex items-center justify-center">
            <Clock className="w-5 h-5 text-gold-400 animate-pulse" />
          </div>
          <div>
            <p className="font-medium text-gold-400">Application Pending</p>
            <p className="text-sm text-muted-foreground">Your application is under review by admin</p>
          </div>
        </div>
      </div>
    );
  }
  
  return null;
};

// Stats Card Component
const StatsCard = ({ title, value, subtitle, icon: Icon, color = 'gold' }) => {
  const colorClasses = {
    gold: 'text-gold-400',
    green: 'text-green-400',
    blue: 'text-blue-400',
    red: 'text-red-400'
  };
  
  return (
    <Card className="glass-card border-white/10">
      <CardContent className="p-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs uppercase tracking-widest text-muted-foreground">{title}</p>
            <p className={`text-3xl font-display mt-1 ${colorClasses[color]}`}>{value}</p>
            {subtitle && <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>}
          </div>
          <Icon className={`w-8 h-8 ${colorClasses[color]}/50`} />
        </div>
      </CardContent>
    </Card>
  );
};

// Listings Tab
const ListingsTab = ({ listings, onCreateNew, onSubmit, onView }) => {
  const STATUS_CONFIG = {
    draft: { color: 'bg-gray-600/20 text-gray-400', label: 'Draft' },
    submitted: { color: 'bg-gold-600/20 text-gold-400', label: 'Submitted' },
    under_review: { color: 'bg-blue-600/20 text-blue-400', label: 'Under Review' },
    approved: { color: 'bg-green-600/20 text-green-400', label: 'Approved' },
    active: { color: 'bg-green-600/20 text-green-400', label: 'Active' },
    rejected: { color: 'bg-red-600/20 text-red-400', label: 'Rejected' }
  };

  if (listings.length === 0) {
    return (
      <div className="text-center py-12">
        <Building2 className="w-12 h-12 mx-auto text-muted-foreground/30 mb-4" />
        <h3 className="text-lg font-medium">No Properties Yet</h3>
        <p className="text-sm text-muted-foreground mt-2">
          Create your first property listing to get started.
        </p>
        <Button className="mt-4 btn-gold-metallic" onClick={onCreateNew}>
          <Plus className="w-4 h-4 mr-2" />
          Add Property
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button className="btn-gold-metallic" onClick={onCreateNew}>
          <Plus className="w-4 h-4 mr-2" />
          Add Property
        </Button>
      </div>
      
      {listings.map((property) => {
        const config = STATUS_CONFIG[property.status] || STATUS_CONFIG.draft;
        
        return (
          <Card key={property.id} className="border-white/10">
            <CardContent className="p-4">
              <div className="flex items-center gap-4">
                <div className="w-24 h-16 rounded bg-white/10 flex items-center justify-center overflow-hidden">
                  {property.images?.[0] ? (
                    <img src={property.images[0]} alt={property.title} className="w-full h-full object-cover" />
                  ) : (
                    <Building2 className="w-8 h-8 text-muted-foreground/30" />
                  )}
                </div>
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h4 className="font-medium truncate">{property.title}</h4>
                    <Badge className={config.color}>{config.label}</Badge>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {property.location?.area} • {property.bedrooms} BR • {property.area_sqft?.toLocaleString()} sqft
                  </p>
                  <p className="text-lg font-display gold-interactive mt-1">
                    {formatCurrency(property.price)}
                  </p>
                </div>
                
                <div className="flex items-center gap-2">
                  {property.status === 'draft' && (
                    <Button 
                      size="sm" 
                      className="btn-gold-metallic"
                      onClick={() => onSubmit(property.id)}
                    >
                      Submit for Review
                    </Button>
                  )}
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => onView(property.id)}
                  >
                    View
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};

// Viewings Tab
const ViewingsTab = ({ viewings, onRespond }) => {
  const STATUS_CONFIG = {
    pending: { color: 'bg-gold-600/20 text-gold-400', label: 'Pending' },
    approved: { color: 'bg-blue-600/20 text-blue-400', label: 'Approved' },
    confirmed: { color: 'bg-green-600/20 text-green-400', label: 'Confirmed' },
    completed: { color: 'bg-green-600/20 text-green-400', label: 'Completed' },
    rejected: { color: 'bg-red-600/20 text-red-400', label: 'Rejected' },
    cancelled: { color: 'bg-gray-600/20 text-gray-400', label: 'Cancelled' }
  };

  if (viewings.length === 0) {
    return (
      <div className="text-center py-12">
        <Eye className="w-12 h-12 mx-auto text-muted-foreground/30 mb-4" />
        <h3 className="text-lg font-medium">No Viewing Requests</h3>
        <p className="text-sm text-muted-foreground mt-2">
          Viewing requests from buyers will appear here.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {viewings.map((viewing) => {
        const config = STATUS_CONFIG[viewing.status] || STATUS_CONFIG.pending;
        
        return (
          <Card key={viewing.id} className="border-white/10">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h4 className="font-medium">{viewing.property_title}</h4>
                    <Badge className={config.color}>{config.label}</Badge>
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">
                    Buyer: {viewing.buyer_name} • {viewing.buyer_email}
                  </p>
                  {viewing.scheduled_date && (
                    <p className="text-sm text-gold-400 mt-1">
                      <Calendar className="w-3 h-3 inline mr-1" />
                      {viewing.scheduled_date} at {viewing.scheduled_time}
                    </p>
                  )}
                </div>
                
                {viewing.status === 'pending' && (
                  <div className="flex items-center gap-2">
                    <Button 
                      size="sm" 
                      className="btn-gold-metallic"
                      onClick={() => onRespond(viewing.id, 'approve')}
                    >
                      <Check className="w-3 h-3 mr-1" />
                      Approve
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline"
                      className="border-red-600/50 text-red-400 hover:bg-red-950/30"
                      onClick={() => onRespond(viewing.id, 'reject')}
                    >
                      <X className="w-3 h-3 mr-1" />
                      Reject
                    </Button>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};

// Offers Tab
const OffersTab = ({ offers, onRespond }) => {
  const STATUS_CONFIG = {
    submitted: { color: 'bg-gold-600/20 text-gold-400', label: 'New' },
    pending: { color: 'bg-gold-600/20 text-gold-400', label: 'Pending' },
    countered: { color: 'bg-blue-600/20 text-blue-400', label: 'Countered' },
    accepted: { color: 'bg-green-600/20 text-green-400', label: 'Accepted' },
    rejected: { color: 'bg-red-600/20 text-red-400', label: 'Rejected' },
    withdrawn: { color: 'bg-gray-600/20 text-gray-400', label: 'Withdrawn' }
  };

  if (offers.length === 0) {
    return (
      <div className="text-center py-12">
        <AEDIcon className="w-12 h-12 mx-auto text-muted-foreground/30 mb-4" />
        <h3 className="text-lg font-medium">No Offers Received</h3>
        <p className="text-sm text-muted-foreground mt-2">
          Offers from buyers will appear here.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {offers.map((offer) => {
        const config = STATUS_CONFIG[offer.status] || STATUS_CONFIG.pending;
        
        return (
          <Card key={offer.id} className="border-white/10">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h4 className="font-medium">{offer.property_title}</h4>
                    <Badge className={config.color}>{config.label}</Badge>
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">
                    From: {offer.buyer_name}
                  </p>
                  
                  <div className="flex items-center gap-6 mt-2">
                    <div>
                      <p className="text-xs text-muted-foreground">Offer</p>
                      <p className="font-display text-lg gold-interactive">{formatCurrency(offer.amount)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Asking</p>
                      <p className="font-display text-lg">{formatCurrency(offer.asking_price)}</p>
                    </div>
                    {offer.counter_amount && (
                      <div>
                        <p className="text-xs text-muted-foreground">Counter</p>
                        <p className="font-display text-lg text-blue-400">{formatCurrency(offer.counter_amount)}</p>
                      </div>
                    )}
                  </div>
                </div>
                
                {['submitted', 'pending', 'countered'].includes(offer.status) && (
                  <div className="flex items-center gap-2">
                    <Button 
                      size="sm" 
                      className="btn-gold-metallic"
                      onClick={() => onRespond(offer.id, 'accept')}
                    >
                      Accept
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => onRespond(offer.id, 'counter')}
                    >
                      Counter
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline"
                      className="border-red-600/50 text-red-400 hover:bg-red-950/30"
                      onClick={() => onRespond(offer.id, 'reject')}
                    >
                      Reject
                    </Button>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};

// Deals Tab
const DealsTab = ({ deals, onViewDeal }) => {
  const STATUS_CONFIG = {
    deal_negotiating: { color: 'bg-gold-600/20 text-gold-400', label: 'Negotiating' },
    deal_offer_accepted: { color: 'bg-blue-600/20 text-blue-400', label: 'Offer Accepted' },
    deal_form_f_pending: { color: 'bg-gold-600/20 text-gold-400', label: 'Form F Pending' },
    deal_form_f_signed: { color: 'bg-green-600/20 text-green-400', label: 'Form F Signed' },
    deal_closed: { color: 'bg-green-600/20 text-green-400', label: 'Closed' },
    deal_dispute_mode: { color: 'bg-red-600/20 text-red-400', label: 'Dispute' },
    deal_frozen: { color: 'bg-red-600/20 text-red-400', label: 'Frozen' }
  };

  if (deals.length === 0) {
    return (
      <div className="text-center py-12">
        <FileText className="w-12 h-12 mx-auto text-muted-foreground/30 mb-4" />
        <h3 className="text-lg font-medium">No Active Deals</h3>
        <p className="text-sm text-muted-foreground mt-2">
          Accepted offers will create deals that appear here.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {deals.map((deal) => {
        const config = STATUS_CONFIG[deal.state] || STATUS_CONFIG.deal_negotiating;
        
        return (
          <Card key={deal.id} className={`border-white/10 ${deal.is_frozen ? 'border-red-600/30' : ''}`}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h4 className="font-medium">{deal.property_title}</h4>
                    <Badge className={config.color}>{config.label}</Badge>
                    {deal.is_frozen && (
                      <Badge className="bg-red-600/20 text-red-400">Frozen</Badge>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">
                    Deal ID: {deal.id?.slice(0, 8).toUpperCase()}
                  </p>
                  <p className="font-display text-lg gold-interactive mt-1">
                    {formatCurrency(deal.agreed_price)}
                  </p>
                </div>
                
                <Button 
                  size="sm" 
                  variant="outline"
                  onClick={() => onViewDeal(deal.id)}
                >
                  View Timeline
                </Button>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};

// Performance Tab
const PerformanceTab = ({ metrics }) => {
  if (!metrics) {
    return (
      <div className="text-center py-12">
        <BarChart3 className="w-12 h-12 mx-auto text-muted-foreground/30 mb-4" />
        <h3 className="text-lg font-medium">Loading Metrics...</h3>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatsCard 
          title="Total Properties" 
          value={metrics.summary?.total_properties || 0} 
          icon={Building2} 
        />
        <StatsCard 
          title="Pending Viewings" 
          value={metrics.summary?.pending_viewings || 0} 
          icon={Eye}
          color="blue"
        />
        <StatsCard 
          title="Pending Offers" 
          value={metrics.summary?.pending_offers || 0} 
          icon={AEDIcon}
          color="gold"
        />
        <StatsCard 
          title="Closed Deals" 
          value={metrics.summary?.closed_deals || 0} 
          icon={FileText}
          color="green"
        />
      </div>
      
      <Card className="border-white/10">
        <CardHeader>
          <CardTitle className="text-lg">Conversion Rates</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-6">
            <div>
              <p className="text-sm text-muted-foreground">Viewing → Offer</p>
              <p className="text-2xl font-display gold-interactive">
                {metrics.conversion_rates?.viewing_to_offer || 0}%
              </p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Offer → Deal</p>
              <p className="text-2xl font-display gold-interactive">
                {metrics.conversion_rates?.offer_to_deal || 0}%
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Main Agent Dashboard Component
const AgentDashboardPage = () => {
  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [agentStatus, setAgentStatus] = useState(null);
  const [listings, setListings] = useState([]);
  const [viewings, setViewings] = useState([]);
  const [offers, setOffers] = useState([]);
  const [deals, setDeals] = useState([]);
  const [metrics, setMetrics] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const userId = user?.id;
      if (!userId) return;

      // Get agent status first
      const statusRes = await api.get(`/agent/status?user_id=${userId}`);
      setAgentStatus(statusRes.data);
      
      // If approved, fetch all data
      if (statusRes.data?.status === 'approved') {
        const [listingsRes, viewingsRes, offersRes, dealsRes, metricsRes] = await Promise.allSettled([
          api.get(`/agent/listings?user_id=${userId}`),
          api.get(`/agent/viewings?user_id=${userId}`),
          api.get(`/agent/offers?user_id=${userId}`),
          api.get(`/agent/deals?user_id=${userId}`),
          api.get(`/agent/performance?user_id=${userId}`)
        ]);
        
        if (listingsRes.status === 'fulfilled') setListings(listingsRes.value.data.properties || []);
        if (viewingsRes.status === 'fulfilled') setViewings(viewingsRes.value.data.viewings || []);
        if (offersRes.status === 'fulfilled') setOffers(offersRes.value.data.offers || []);
        if (dealsRes.status === 'fulfilled') setDeals(dealsRes.value.data.deals || []);
        if (metricsRes.status === 'fulfilled') setMetrics(metricsRes.value.data);
      }
    } catch (error) {
      console.error('Failed to fetch agent data:', error);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSubmitProperty = async (propertyId) => {
    try {
      await api.post(`/agent/property/${propertyId}/submit?user_id=${user?.id}`);
      toast.success('Property submitted for review');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit property');
    }
  };

  const handleViewingResponse = async (viewingId, action) => {
    try {
      const data = { action };
      if (action === 'approve') {
        // For now, use a default time - in production, show a dialog
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        data.scheduled_date = tomorrow.toISOString().split('T')[0];
        data.scheduled_time = '10:00';
      }
      
      await api.post(`/agent/viewings/${viewingId}/respond?user_id=${user?.id}`, data);
      toast.success(`Viewing ${action}ed`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to respond to viewing');
    }
  };

  const handleOfferResponse = async (offerId, action) => {
    try {
      const data = { action };
      if (action === 'counter') {
        // For now, just show a message - in production, show a dialog
        toast.info('Counter offer dialog coming soon');
        return;
      }
      
      await api.post(`/agent/offers/${offerId}/respond?user_id=${user?.id}`, data);
      toast.success(`Offer ${action}ed`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to respond to offer');
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <RefreshCw className="w-8 h-8 animate-spin text-gold-400" />
      </div>
    );
  }

  // Not an agent yet - show application prompt
  if (!agentStatus?.is_agent) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <Card className="max-w-md w-full border-white/10">
          <CardHeader className="text-center">
            <div className="w-16 h-16 mx-auto rounded-full bg-gold-600/20 flex items-center justify-center mb-4">
              <Users className="w-8 h-8 text-gold-400" />
            </div>
            <CardTitle>Become an Agent</CardTitle>
            <CardDescription>
              Apply to become a verified agent on Rashii platform
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button 
              className="w-full btn-gold-metallic"
              onClick={() => navigate('/agent/apply')}
            >
              Apply Now
            </Button>
            <Button 
              variant="outline" 
              className="w-full mt-3"
              onClick={() => navigate('/')}
            >
              Back to Home
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const pendingViewings = viewings.filter(v => v.status === 'pending').length;
  const pendingOffers = offers.filter(o => ['submitted', 'pending'].includes(o.status)).length;

  return (
    <div className="min-h-screen bg-background" data-testid="agent-dashboard-page">
      {/* Header */}
      <header className="border-b border-border sticky top-0 bg-background/95 backdrop-blur z-50">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
                <ChevronLeft className="w-4 h-4 mr-1" />
                Back
              </Button>
              <Button variant="ghost" size="sm" onClick={() => navigate('/')}>
                <Home className="w-4 h-4 mr-1" />
                Home
              </Button>
              {/* Rashii Logo - Consistent sizing */}
              <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/')}>
                <div className="w-10 h-10 rounded logo-icon-metallic flex items-center justify-center">
                  <span className="font-display text-lg text-primary-foreground font-bold">R</span>
                </div>
                <span className="font-display text-xl font-medium tracking-tight gold-interactive">Rashii</span>
              </div>
              <Badge variant="gold">
                AGENT
              </Badge>
            </div>
            
            <div className="flex items-center gap-4">
              <Badge variant={agentStatus?.status === 'approved' ? 'default' : 'outline'} 
                     className={agentStatus?.status === 'approved' ? 'bg-green-600/20 text-green-400' : 'border-gold-600/30'}>
                {agentStatus?.status === 'approved' ? 'Verified' : agentStatus?.status}
              </Badge>
              <Button variant="ghost" size="icon">
                <Bell className="w-5 h-5" />
              </Button>
              <Button variant="ghost" size="icon">
                <Settings className="w-5 h-5" />
              </Button>
              <span className="text-sm text-muted-foreground">{user?.email}</span>
              <Button variant="ghost" size="sm" onClick={handleLogout}>
                <LogOut className="w-4 h-4 mr-1" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-6 py-6">
        {/* Status Banner */}
        <AgentStatusBanner status={agentStatus?.status} agencyName={agentStatus?.agency_name} />

        {agentStatus?.status === 'approved' ? (
          <>
            {/* Tabs */}
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="bg-white/5 border border-white/10 mb-6">
                <TabsTrigger value="overview">Overview</TabsTrigger>
                <TabsTrigger value="listings">My Listings</TabsTrigger>
                <TabsTrigger value="viewings">
                  Viewings {pendingViewings > 0 && <Badge className="ml-1 bg-gold-600">{pendingViewings}</Badge>}
                </TabsTrigger>
                <TabsTrigger value="offers">
                  Offers {pendingOffers > 0 && <Badge className="ml-1 bg-gold-600">{pendingOffers}</Badge>}
                </TabsTrigger>
                <TabsTrigger value="analytics">Analytics</TabsTrigger>
              </TabsList>

              {/* Overview Tab */}
              <TabsContent value="overview" className="space-y-6">
                {/* Performance Score - Soft Visibility */}
                <AgentPerformanceScore 
                  token={localStorage.getItem('rashii_access_token')} 
                  agentId={user?.id} 
                />

                <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                  <StatsCard 
                    title="Total Properties" 
                    value={listings.length} 
                    icon={Building2} 
                  />
                  <StatsCard 
                    title="Viewing Slots Left" 
                    value={listings.reduce((acc, p) => acc + (p.viewing_slots_remaining || 0), 0)}
                    subtitle={`of ${listings.reduce((acc, p) => acc + (p.viewing_slots_total || 0), 0)}`}
                    icon={Eye}
                    color="blue"
                  />
                  <StatsCard 
                    title="Times Selected" 
                    value={viewings.filter(v => v.status === 'completed').length}
                    icon={Users}
                    color="gold"
                  />
                  <StatsCard 
                    title="Pending Offers" 
                    value={pendingOffers}
                    icon={AEDIcon}
                    color="green"
                  />
                  <StatsCard 
                    title="Conversion Rate" 
                    value={`${metrics?.conversion_rates?.viewing_to_offer || 0}%`}
                    icon={TrendingUp}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Card className="border-white/10">
                    <CardHeader>
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Building2 className="w-5 h-5 text-gold-400" />
                        Recent Listings
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {listings.length === 0 ? (
                        <div className="text-center py-6">
                          <p className="text-muted-foreground">No properties yet</p>
                          <Button 
                            className="mt-3 btn-gold-metallic"
                            onClick={() => navigate('/agent/property/new')}
                          >
                            <Plus className="w-4 h-4 mr-2" />
                            Add Property
                          </Button>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          {listings.slice(0, 3).map(p => (
                            <div key={p.id} className="flex items-center justify-between p-3 rounded bg-white/5">
                              <div>
                                <p className="font-medium truncate">{p.title}</p>
                                <p className="text-sm text-muted-foreground">{formatCurrency(p.price)}</p>
                              </div>
                              <Badge>{p.status}</Badge>
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  <Card className="border-white/10">
                    <CardHeader>
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Clock className="w-5 h-5 text-gold-400" />
                        Pending Actions
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {pendingViewings === 0 && pendingOffers === 0 ? (
                        <div className="text-center py-6">
                          <Check className="w-8 h-8 mx-auto text-green-400 mb-2" />
                          <p className="text-muted-foreground">No pending actions</p>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          {pendingViewings > 0 && (
                            <div className="flex items-center justify-between p-3 rounded bg-gold-950/20 border border-gold-600/30">
                              <div className="flex items-center gap-3">
                                <Eye className="w-5 h-5 text-gold-400" />
                                <span>{pendingViewings} viewing request(s)</span>
                              </div>
                              <Button size="sm" variant="outline" onClick={() => setActiveTab('viewings')}>
                                Review
                              </Button>
                            </div>
                          )}
                          {pendingOffers > 0 && (
                            <div className="flex items-center justify-between p-3 rounded bg-green-950/20 border border-green-600/30">
                              <div className="flex items-center gap-3">
                                <AEDIcon className="w-5 h-5 text-green-400" />
                                <span>{pendingOffers} pending offer(s)</span>
                              </div>
                              <Button size="sm" variant="outline" onClick={() => setActiveTab('offers')}>
                                Review
                              </Button>
                            </div>
                          )}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>

              {/* Listings Tab */}
              <TabsContent value="listings">
                <Card className="border-white/10 bg-card/50">
                  <CardHeader>
                    <CardTitle className="font-display text-xl">My Listings</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ListingsTab 
                      listings={listings}
                      onCreateNew={() => navigate('/agent/property/new')}
                      onSubmit={handleSubmitProperty}
                      onView={(id) => navigate(`/properties/${id}`)}
                    />
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Viewings Tab */}
              <TabsContent value="viewings">
                <Card className="border-white/10 bg-card/50">
                  <CardHeader>
                    <CardTitle className="font-display text-xl">Viewing Requests</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ViewingsTab 
                      viewings={viewings}
                      onRespond={handleViewingResponse}
                    />
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Offers Tab */}
              <TabsContent value="offers">
                <Card className="border-white/10 bg-card/50">
                  <CardHeader>
                    <CardTitle className="font-display text-xl">Offers</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <OffersTab 
                      offers={offers}
                      onRespond={handleOfferResponse}
                    />
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Analytics Tab */}
              <TabsContent value="analytics">
                <Card className="border-white/10 bg-card/50">
                  <CardHeader>
                    <CardTitle className="font-display text-xl">Performance Analytics</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <PerformanceTab metrics={metrics} />
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </>
        ) : (
          <div className="text-center py-12">
            <Clock className="w-16 h-16 mx-auto text-gold-400 mb-4" />
            <h2 className="text-2xl font-display">Application Under Review</h2>
            <p className="text-muted-foreground mt-2">
              Your agent application is being reviewed. You'll be notified once approved.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AgentDashboardPage;
